import { Button, ButtonGroup, Grid, Header, Message } from 'semantic-ui-react';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { errorUtils } from 'utils/errorUtils';
import DropZone from 'components/dropzone/DropZone';
import { FileRejection } from 'react-dropzone';
import { MiGelFileRequestDto, MiGelFileResultDto, MiGelImportRequest } from 'ts-types/api.types';
import { miGelImport, previewMiGelDataFromFile } from 'services/miGelImportServices';

interface Props extends MessageSourceProps {
}

interface State {
  miGelImportData: Array<MiGelFileResultDto>,
  previewLoaded: boolean,
  importSuccessMsg?: string,
  errorMessages: string[],
  cancelTokenSource: CancelTokenSource,
}

class MiGelImportView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      miGelImportData: [],
      previewLoaded: true,
      errorMessages: [],
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  cancelImport = (form: FormApi) => {

    this.setErrorMessage();

    form.change("file", undefined);
    this.setState({
      miGelImportData: []
    })
  };

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {
    form.change("file", acceptedFiles[0]);
    this.setState({
      importSuccessMsg: undefined
    })
  };

  onDropRejected = (rejections: FileRejection[]) => {
    console.log(rejections, "FILE REJECTIONS");
  };

  previewFromFile = (form: FormApi) => {
    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        previewLoaded: true
      })
    };

    const values: Partial<MiGelFileRequestDto> = form.getState().values;

    this.setState({previewLoaded: false});

    previewMiGelDataFromFile(values, cancelTokenSource)
        .then(response => {
              this.setState({
                miGelImportData: response
              })
            }
        )
        .catch((e) => this.handleError(e.response.data))
        .finally(onFinally);
  }

  clearFormValues = (form: FormApi) => {

    form.change("file", undefined);

    this.setState({
      miGelImportData: [],
      importSuccessMsg: undefined
    });
  }

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {

      this.setState({
        errorMessages: []
      });
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.invalidFile,
      errorUtils.canNotImportFileIsEmpty
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    }

    if (!this.state.errorMessages.length) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`));
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  handleSubmit = async (values: any) => {
    const {getMessage} = this.props;
    const {miGelImportData, cancelTokenSource} = this.state;

    const onSave = () => this.setState({
      importSuccessMsg: `${getMessage('miGelImport.successMsg')}`,
      previewLoaded: true
    })

    if (miGelImportData && miGelImportData.length > 0) {

      const request: Partial<MiGelImportRequest> = {
        miGelFileResultDtoList: miGelImportData
      }

      await miGelImport(request, cancelTokenSource)
          .then(response => {
            values.file = undefined;
            this.setState({
              miGelImportData: []
            })
          })
          .catch((e: any) => this.handleError(e.response.data))
          .finally(onSave)
    }
  };

  render(): React.ReactNode {
    const {previewLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderTopSegment()}
          </div>
          <StyledViewTableRow>
            {!previewLoaded
                ? <LoaderComponent message={getMessage("miGelImport.loading")}/>
                : this.renderMiGelImportTable()}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderTopSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={{}}
            subscription={{pristine: true, values: true, submitting: true}}
            render={this.renderImportForm}
        />
    );
  };

  renderImportForm = ({form, handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {
      importSuccessMsg,
      miGelImportData
    } = this.state;

    const uploadMessage = values.file ? values.file.name : getMessage("miGelImport.upload");

    const disablePreview = (!values.file);
    const disableClear = (!values.file && !importSuccessMsg);
    const disableImport = !(miGelImportData.length > 0) || submitting;

    return (
        <form onSubmit={handleSubmit}>
          <PxGrid>
            {
                importSuccessMsg &&
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message success onDismiss={() => this.setState({importSuccessMsg: undefined})}>
                      <p>
                        {importSuccessMsg}
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={16} verticalAlign="middle">
                <Header size="tiny">{getMessage("miGelImport.import")}</Header>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={5} verticalAlign="middle">
                <DropZone
                    multiple={false}
                    disabled={false}
                    message={uploadMessage}
                    onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                    onDropRejected={this.onDropRejected}
                />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="bottom">
                <ButtonGroup className="float-left">
                  <Button
                      compact
                      type="button"
                      disabled={disablePreview}
                      onClick={() => this.previewFromFile(form)}
                  >
                    {getMessage("miGelImport.preview")}
                  </Button>
                  <Button
                      compact
                      color="red"
                      type="button"
                      disabled={disableClear}
                      onClick={() => this.clearFormValues(form)}
                  >
                    {getMessage("miGelImport.clear")}
                  </Button>
                </ButtonGroup>
              </Grid.Column>
              <Grid.Column width={1} primary verticalAlign="bottom" textAlign={'left'}>
                <Button
                    primary
                    compact
                    type="submit"
                    disabled={disableImport}
                >
                  {getMessage("miGelImport.import")}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </PxGrid>
        </form>
    );
  };

  miGelImportRowGetter = ({index}: any) => {
    const {miGelImportData} = this.state;

    Object.assign(miGelImportData[index], {index: index + 1});

    return miGelImportData[index]
  };

  miGelImportRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderMiGelImportTable = (): JSX.Element => {
    const {getMessage} = this.props;
    const {miGelImportData} = this.state;

    return (
        <VirtualizedTable
            rowCount={miGelImportData.length}
            rowGetter={this.miGelImportRowGetter}
            rowRenderer={this.miGelImportRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "#",
                dataKey: "index",
              },
              {
                width: 100,
                label: (getMessage("miGelImport.medicamentId")),
                dataKey: "medicamentId"
              },
              {
                width: 100,
                label: (getMessage("miGelImport.code")),
                dataKey: "code"
              },
              {
                width: 150,
                label: (getMessage("miGelImport.unitQty")),
                dataKey: "unitQty"
              },
              {
                width: 150,
                label: (getMessage("miGelImport.billingPrice")),
                dataKey: "billingPrice"
              },
              {
                width: 150,
                label: (getMessage("miGelImport.doctorsPrice")),
                dataKey: "doctorsPrice"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("miGelImport.descriptionFr")),
                dataKey: "descriptionFr"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("miGelImport.descriptionIt")),
                dataKey: "descriptionIt"
              },
              {
                width: 150,
                label: (getMessage("miGelImport.effectiveDate")),
                dataKey: "effectiveDate"
              }
            ]}
        />
    );
  };

}

export default withMessages(MiGelImportView);
